import { BaseMeiucaService } from './MeiucaService'
import { env, apiEndpoints } from '@/lib/env'
import type { PaginatedResponse } from './pagination'
import type { WorkoutListItem } from './workoutService'

// ---------------------------------------------------------------------------
// Types — mirror the `meiuca_app/v1` makeups + self-enrollment DTOs
// ---------------------------------------------------------------------------

/** Item of `GET /me/makeups/grouped-by-training-center`. */
export interface MakeupTrainingCenterGroup {
  trainingCenterId: string
  trainingCenterName: string
  count: number
  nextExpiresAt?: string | null
  expiringSoonCount: number
}

/**
 * Resposta de `GET /training-centers/{id}/self-enrollment-agenda`.
 *
 * Sem paginação: a janela é de no máximo 7 dias e vem inteira. Só entram os
 * dias em que o aluno tem alguma aula para marcar — a tela monta o seletor de
 * dias direto daqui, em vez de adivinhar quais dias existem.
 */
export interface SelfEnrollmentAgenda {
  days: SelfEnrollmentAgendaDay[]
}

/**
 * Se a aula ainda aceita o aluno entrando — resolvido pelo servidor. Sem isto,
 * cada cliente refazia a conta de `date + startTimeMin` e da lotação, e o
 * checkout respondia com outra.
 */
export interface SelfEnrollmentStatus {
  available: boolean
  /** `occurrenceNotJoinable` (cancelada ou já começou) | `occurrenceFull`. */
  blockReason?: string | null
}

export interface SelfEnrollmentAgendaDay {
  date: string
  occurrences: WorkoutListItem[]
}

/** Item of `GET /me/makeups` (flat credit list). */
export interface MakeupCreditListItem {
  id: string
  trainingCenterId: string
  becomesUsableAt: string
  expiresAt: string
  /** Modality the credit is scoped to; null/absent = wildcard (any modality). */
  modality?: string | null
}

/**
 * Block reasons of the self-enroll checkout. NOTE: the backend enum encodes
 * its Swift case names, so the wire values are camelCase (not snake_case).
 */
export type MakeupCheckoutBlockReason =
  | 'occurrenceNotFound'
  | 'occurrenceNotJoinable'
  | 'selfEnrollmentNotAllowed'
  | 'levelNotAllowed'
  | 'occurrenceFull'
  | 'participantAlreadyExists'
  | 'customerNotFound'
  | 'noMakeUpCredit'
  | 'noUsableMakeUpCredit'
  | 'noMakeUpCreditForModality'

export interface MakeupCheckoutOptions {
  mode: 'makeup' | 'blocked'
  blockReason?: MakeupCheckoutBlockReason | null
  makeup?: {
    availableCredits: number
    nextExpiringAt?: string | null
  } | null
  /**
   * On `blocked` for lack of credit, the catalog of class packages the center
   * sells through the app — the way out of the block is buying credits.
   */
  packagesForSale?: {
    id: string
    name: string
    classCount: number
    priceCents: number
    validityDays: number
  }[] | null
}

export interface MakeupSelfEnrollResult {
  participant: {
    id: string
    trainingCenterId: string
    trainingCenterClassOccurrenceId: string
    trainingCenterCustomerId: string
    type: string
    source: string
    status: string
    confirmedAt?: string | null
  }
  customerMakeUpCreditId: string
}

class MakeupService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: apiEndpoints.auth.refresh,
      cookiePrefix: '',
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }

  /** Available makeup credits grouped per training center. */
  async listCreditsGroupedByTrainingCenter(): Promise<MakeupTrainingCenterGroup[]> {
    const response = await this.get<{ data: { data: MakeupTrainingCenterGroup[] } }>(
      '/me/makeups/grouped-by-training-center'
    )
    return response.data.data.data
  }

  /** Flat list of available credits (cursor paginated, no envelope). */
  async listCredits(params?: {
    trainingCenterId?: string
    limit?: number
    cursor?: string
  }): Promise<PaginatedResponse<MakeupCreditListItem>> {
    const queryParams = new URLSearchParams()
    if (params?.trainingCenterId) queryParams.append('trainingCenterId', params.trainingCenterId)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.cursor) queryParams.append('cursor', params.cursor)

    const query = queryParams.toString()
    const response = await this.get<PaginatedResponse<MakeupCreditListItem>>(
      `/me/makeups${query ? `?${query}` : ''}`
    )
    return response.data
  }

  /**
   * Agenda de auto-agendamento: os dias, dentro da janela, em que o aluno tem
   * aula para marcar neste CT.
   *
   * As regras de elegibilidade são do endpoint — aqui só vai o recorte de
   * datas, limitado a 7 dias pelo backend (janela maior é 400).
   */
  async getSelfEnrollmentAgenda(
    trainingCenterId: string,
    params: { dateFrom: string; dateTo: string }
  ): Promise<SelfEnrollmentAgenda> {
    const queryParams = new URLSearchParams({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo
    })

    const response = await this.get<{ data: SelfEnrollmentAgenda }>(
      `/training-centers/${encodeURIComponent(trainingCenterId)}/self-enrollment-agenda?${queryParams}`
    )
    return response.data.data
  }

  /** Preflight of the self-enroll: whether a credit can be used, or why not. */
  async getCheckoutOptions(
    trainingCenterId: string,
    occurrenceId: string
  ): Promise<MakeupCheckoutOptions> {
    const response = await this.post<{ data: MakeupCheckoutOptions }>(
      `/training-centers/${encodeURIComponent(trainingCenterId)}/class-occurrences/${encodeURIComponent(occurrenceId)}/self-enroll-checkout-options`,
      {}
    )
    return response.data.data
  }

  /** Enrolls the student consuming one makeup credit. */
  async selfEnroll(
    trainingCenterId: string,
    occurrenceId: string
  ): Promise<MakeupSelfEnrollResult> {
    const response = await this.post<{ data: MakeupSelfEnrollResult }>(
      `/training-centers/${encodeURIComponent(trainingCenterId)}/class-occurrences/${encodeURIComponent(occurrenceId)}/self-enroll`,
      {}
    )
    return response.data.data
  }
}

export const makeupService = new MakeupService()
